import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Form, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { NotificationContainer, NotificationManager } from "react-notifications";
import LoadingSpinner from "@gull/components/Loading/LoadingSpinner";
import { getQueryPostDto, getQuery } from "../ServiceController";
import { Breadcrumb } from "@gull";
import { formattaDataOraHHmmss } from "app/utility/dataOra";
import VisualizzaRicettaModal from "./VisualizaRicettaModal";
import { ricercaErogato } from "./nreService";
import { ElencoErroriRicetta } from "./ElencoErroriRicetta";

const getLocalTodayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const GestioneRicetteNre = () => {
  const dispatch = useDispatch();

  const todayIso = useMemo(() => getLocalTodayIso(), []);

  const [isLoading, setLoading] = useState(false);
  const [responseVisualizza, setResponseVisualizza] = useState();
  const [show, setShow] = useState(false);
  const [erroriRicetta, setErroriRicetta] = useState(null);

  // filtri
  const [registri, setRegistri] = useState([]);
  const [idRegistro, setIdRegistro] = useState(0);

  // ✅ inizializzate con data odierna
  const [dal, setDal] = useState(todayIso);
  const [al, setAl] = useState(todayIso);

  const [nre, setNre] = useState("");
  const [statoRicetta, setStatoRicetta] = useState("E"); // obbligatorio (P/E) quando NON si fa ricerca puntuale NRE
  const [tipoData, setTipoData] = useState("E"); // E=erogazione, T=trasmissione (quando NON si fa ricerca puntuale NRE)

  // risultati
  const [records, setRecords] = useState([]);

  // ---- badge mapping (stati numerici) ----
  const badgeMap = useMemo(
    () => ({
      3: { cls: "bg-secondary", label: "DA EROGARE" },
      4: { cls: "bg-danger", label: "ANNULLATA DAL PRESCRITTORE" },
      5: { cls: "bg-info", label: "PRESA IN CARICO" },
      6: { cls: "bg-warning", label: "SOSPESA" },
      7: { cls: "bg-primary", label: "SINGOLA PRESCRIZIONE EROGATA" },
      8: { cls: "bg-success", label: "RICETTA EROGATA" },
      9: { cls: "bg-dark", label: "RI-EROGATA DOPO ANNULLAMENTO" },
    }),
    []
  );

  const getBadgeStato = (statoNumerico) => {
    const key = Number(statoNumerico);
    const info = badgeMap[key] || { cls: "bg-light text-dark", label: "N/D" };

    return (
      <span>
        <span className={`badge ${info.cls} p-2 me-2`}>
          {Number.isFinite(key) ? key : "?"}
        </span>
        {info.label}
      </span>
    );
  };

  // --- helpers date ---
  const parseIsoDate = (s) => {
    if (!s) return null;
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const daysDiffInclusive = (d1, d2) => {
    const ms = d2.getTime() - d1.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    return days + 1;
  };

  const isSameMonth = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();

  const clearNotifications = () => {
    NotificationManager.listNotify?.forEach((n) =>
      NotificationManager.remove({ id: n.id })
    );
  };

  // ✅ NRE valido SOLO se lungo 15 caratteri
  const nreTrim = (nre || "").trim();
  const hasNre = nreTrim.length > 0;
  const isNreValid = !hasNre || nreTrim.length === 15;

  /**
   * ✅ La ricerca può partire SOLO in questi 3 casi:
   * 1) tutti i filtri valorizzati (incl. NRE valido)
   * 2) tutti i filtri tranne NRE valorizzati
   * 3) solo NRE (valido 15) e idRegistro valorizzati (ricerca puntuale)
   *
   * Nota: quando fai ricerca puntuale (caso 3) non obblighiamo DAL/AL/stato/tipo.
   */
  const validateFiltri = () => {
    // registro sempre necessario
    if (!Number(idRegistro) || Number(idRegistro) <= 0) {
      return {
        error: "Seleziona un Registro valido prima di effettuare la ricerca.",
        mode: null,
      };
    }

    // Se NRE valorizzato => deve essere lungo 15
    if (hasNre && nreTrim.length !== 15) {
      return {
        error: `NRE non valido: deve essere lungo ESATTAMENTE 15 caratteri (attuali: ${nreTrim.length}).`,
        mode: null,
      };
    }

    const hasDal = !!dal;
    const hasAl = !!al;

    // --- Caso 3: solo NRE + registro (ricerca puntuale) ---
    // Permettiamo la ricerca anche se date/stato/tipo non sono valorizzati.
    if (hasNre && (!hasDal || !hasAl)) {
      return {
        error: null,
        mode: "NRE_ONLY",
        info:
          "Ricerca puntuale per NRE: verranno ignorati i filtri data/stato/tipo (se non valorizzati).",
      };
    }

    // --- Caso 1 e 2: ricerca per intervallo (date obbligatorie) ---
    if (!hasDal || !hasAl) {
      return {
        error:
          "DAL e AL sono obbligatori per la ricerca per intervallo. In alternativa, compila SOLO NRE (15 caratteri) per la ricerca puntuale.",
        mode: null,
      };
    }

    if (!statoRicetta || (statoRicetta !== "P" && statoRicetta !== "E")) {
      return {
        error:
          'statoRicetta non valido: usare "P" (prese in carico) o "E" (erogazione terminata).',
        mode: null,
      };
    }

    if (!tipoData || (tipoData !== "E" && tipoData !== "T")) {
      return {
        error: 'tipoData non valido: usare "E" (erogazione) o "T" (trasmissione).',
        mode: null,
      };
    }

    const dDal = parseIsoDate(dal);
    const dAl = parseIsoDate(al);
    if (!dDal || !dAl) {
      return { error: "Formato date non valido.", mode: null };
    }
    if (dDal.getTime() > dAl.getTime()) {
      return { error: "DAL non può essere maggiore di AL.", mode: null };
    }

    // regole:
    // - stesso mese: qualsiasi intervallo
    // - a cavallo tra mesi: max 7 giorni inclusivi
    if (!isSameMonth(dDal, dAl)) {
      const diffInc = daysDiffInclusive(dDal, dAl);
      if (diffInc > 7) {
        return {
          error:
            "Per periodi a cavallo tra due mesi, l'intervallo massimo consentito è 7 giorni (inclusivi).",
          mode: null,
        };
      }
    }

    return {
      error: null,
      mode: hasNre ? "ALL_FILTERS" : "ALL_EXCEPT_NRE",
      info: hasNre
        ? "Ricerca con tutti i filtri: intervallo + NRE puntuale."
        : "Ricerca con filtri completi (senza NRE): intervallo + stato + tipo data.",
    };
  };

  const resetFiltri = () => {
    setDal(todayIso);
    setAl(todayIso);
    setNre("");
    setStatoRicetta("P");
    setTipoData("E");
    setRecords([]);
  };

  const buildPayload = (overrideNre) => {
    const finalNre = (overrideNre ?? nreTrim).trim();

    return {
      dal: dal || null,
      al: al || null,
      idRegistro: Number(idRegistro) || 0,
      nre: finalNre,
      statoRicetta: statoRicetta || null,
      tipoData: tipoData || null,
    };
  };

  const handleRicerca = async () => {
    clearNotifications();

    const v = validateFiltri();
    if (v?.error) {
      NotificationManager.warning(v.error, "Ricerca non avviata");
      return;
    }

    const payload = buildPayload();

    setLoading(true);
    try {
      const res = await dispatch(ricercaErogato(payload));

      if (!res?.isSuccess) {
        // NotificationManager.error(
        //   res?.ui?.userMessage || res?.message || "Errore durante la ricerca",
        //   "Errore"
        // );
        setRecords([]);
        setErroriRicetta(res);
        return;
      }

      const data = res?.data?.elencoRicette;
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      // qui restano solo errori davvero “inaspettati”
      console.error(e);
      NotificationManager.error("Errore durante la chiamata al server", "Errore");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };


  // Visualizza (POST con NRE)
  const handleVisualizza = async (codiceNre) => {
    clearNotifications();

    const codice = (codiceNre || "").trim();
    if (!codice) {
      NotificationManager.warning("NRE mancante", "Attenzione");
      return;
    }
    if (codice.length !== 15) {
      NotificationManager.warning(
        `NRE non valido: deve essere lungo ESATTAMENTE 15 caratteri (attuali: ${codice.length}).`,
        "Attenzione"
      );
      return;
    }
    if (!Number(idRegistro) || Number(idRegistro) <= 0) {
      NotificationManager.warning("Seleziona un Registro valido.", "Attenzione");
      return;
    }

    const payload = buildPayload(codice);

    setLoading(true);
    try {
      const res = await dispatch(
        getQueryPostDto("Nre/VisualizzaRicettaDaRicerca", payload)
      );
      if (!res?.isSuccess) {
        NotificationManager.error(
          res?.message || "Errore durante la visualizzazione",
          "Errore"
        );
        return;
      }

      setResponseVisualizza(res?.data);
      setShow(true);
      console.log("Dettaglio ricetta:", res?.data);

      //NotificationManager.success("Dettaglio ricetta caricato", "OK");
    } catch (e) {
      console.error(e);
      NotificationManager.error("Errore chiamata server", "Errore");
    } finally {
      setLoading(false);
    }
  };

  // carico registri
  useEffect(() => {
    dispatch(getQuery("RegistriIva/ElencoRegistriIva"))
      .then((data) => {
        const list = data || [];
        setRegistri(list);
        if (list.length > 0) setIdRegistro(Number(list[0].idregistro));
      })
      .catch((err) => {
        console.error("Errore nel caricamento dei registri:", err);
        NotificationManager.error(
          "Errore nel caricamento dei registri IVA",
          "Errore"
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // colonne (in stile RiepilogoNre: array + map su th)
  const colonneFisse = [
    { key: "__progressivo", label: "#", width: 60 },
    { key: "nre", label: "NRE" },
    { key: "__statoBadge", label: "Stato ricetta" },
    { key: "dataPresaCarico", label: "Data presa in carico" },
    { key: "dataErogazione", label: "Data erogazione" },
    { key: "__azioni", label: "Azioni" },
  ];

  const handleClose = () => {
    setShow(false);
    setResponseVisualizza();
  };

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Contabilità", path: "/contabilita" },
          { name: "Gestione Ricette NRE" },
        ]}
      />

      {/* stile simile a RiepilogoNre: contenitore e form con margine */}
      <div className="mt-4">
        <Form className="mb-3">
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Registro *</Form.Label>
                <Form.Select
                  className="form-control"
                  onChange={(e) => setIdRegistro(Number(e.target.value))}
                  value={idRegistro}
                >
                  {registri.map((r) => (
                    <option key={r.idregistro} value={r.idregistro}>
                      {r.idregistro} - {r.descrizione}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>DAL</Form.Label>
                <Form.Control
                  type="date"
                  value={dal}
                  onChange={(e) => setDal(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>AL</Form.Label>
                <Form.Control
                  type="date"
                  value={al}
                  onChange={(e) => setAl(e.target.value)}
                />
              </Form.Group>
            </Col>

            {/* <Col md={2}>
              <Form.Group>
                <Form.Label>Stato ricetta</Form.Label>
                <Form.Select
                  className="form-control"
                  value={statoRicetta}
                  onChange={(e) => setStatoRicetta(e.target.value)}
                >
                  <option value="P">P - Prese in carico</option>
                  <option value="E">E - Erogazione terminata</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo data</Form.Label>
                <Form.Select
                  className="form-control"
                  value={tipoData}
                  onChange={(e) => setTipoData(e.target.value)}
                >
                  <option value="E">E - Erogazione</option>
                  <option value="T">T - Trasmissione</option>
                </Form.Select>
              </Form.Group>
            </Col> */}
            <Col md={2}>
              <Form.Group>
                <Form.Label>NRE (ricerca puntuale)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Inserisci NRE (15 caratteri)..."
                  value={nre}
                  maxLength={15}
                  onChange={(e) => setNre(e.target.value)}
                  isInvalid={hasNre && !isNreValid}
                />
                <Form.Control.Feedback type="invalid">
                  NRE non valido: deve essere lungo esattamente 15 caratteri
                  (attuali: {nreTrim.length}).
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Row className="mt-3">
              <Col md={12} className="d-flex justify-content-end">
                <div className="d-flex justify-content-end gap-2 my-2">
                  <Button variant="secondary" onClick={resetFiltri} disabled={isLoading}>
                    Azzera Filtri
                  </Button>
                  <Button variant="primary" onClick={handleRicerca} disabled={isLoading}>
                    Ricerca Ricette
                  </Button>
                </div>
              </Col>
              <small className="text-muted d-block mt-2">
                Regole date: stesso mese = qualsiasi intervallo; a cavallo tra mesi = max 7 giorni (inclusivi).
              </small>
            </Row>
          </Row>

          {/* bottoni allineati a destra come RiepilogoNre */}
          <Row className="mt-3">

          </Row>
        </Form>

        {/* spinner come RiepilogoNre */}
        {isLoading && <LoadingSpinner />}

        {/* tabella stile RiepilogoNre */}
        <Table striped hover responsive>
          <thead>
            <tr>
              {colonneFisse.map((c) => (
                <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={colonneFisse.length} className="text-center">
                  Caricamento...
                </td>
              </tr>
            ) : (records?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={colonneFisse.length} className="text-center">
                  Nessun dato disponibile
                </td>
              </tr>
            ) : (
              records.map((r, idx) => (
                <tr key={`${r.nre || "ND"}-${idx}`}>
                  {/* # */}
                  <td>{idx + 1}</td>

                  {/* NRE */}
                  <td className="fw-semibold">
                    {r.nre || <span className="text-muted">N/D</span>}
                  </td>

                  {/* Stato */}
                  <td>
                    <span className="w-100">{getBadgeStato(r.statoRicetta)}</span>
                  </td>

                  {/* Data presa carico */}
                  <td>{formattaDataOraHHmmss(r.dataPresaCarico)}</td>

                  {/* Data erogazione */}
                  <td>{formattaDataOraHHmmss(r.dataErogazione)}</td>

                  {/* Azioni */}
                  <td>
                    <Button
                      variant="info"
                      className="text-white"
                      onClick={() => handleVisualizza(r.nre)}
                      disabled={!r.nre || String(r.nre).trim().length !== 15}
                      title="Visualizza dettaglio ricetta"
                    >
                      <i className="i-Eye me-1"></i> Visualizza
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {!isLoading && (records?.length ?? 0) > 0 && (
            <tfoot className="bg-light fw-bold">
              <tr>
                <td colSpan={colonneFisse.length - 1} className="text-end">
                  TOTALE RICETTE:
                </td>
                <td className="text-primary">{records.length}</td>
              </tr>
            </tfoot>
          )}
        </Table>

        <NotificationContainer />
      </div>

      {responseVisualizza && (
        <VisualizzaRicettaModal
          show={show}
          response={responseVisualizza}
          onClose={handleClose}
          onRicarica={handleRicerca}
        />
      )}

      {erroriRicetta &&
        ElencoErroriRicetta(erroriRicetta, {
          title: "Errori Ricerca",
          onClose: () => setErroriRicetta(null)
        })}
    </div>
  );
};

export default GestioneRicetteNre;